import React from 'react';
import { Box, Button, CircularProgress, Typography, Alert } from '@mui/material';
import store from 'store';
import { setCurrentTeacher } from 'store/actions/authActions';
import Users from 'Api/Users';
import {
    idpForSsoType,
    restoreEmbedSession,
    silentSignIn,
    popupSignIn,
    onEmbedSessionExpired,
} from 'utils/lsgoAuthEmbedSession';

/**
 * Signs a user in through lsgo-authentication inside a SIS iframe route
 * (School Bytes / Sentral), for schools on the new auth. Rendered by
 * SSORoute (and Login.js for SIS SSO links opened in the iframe) instead of
 * the legacy SSO redirect; once the store has the teacher, SSORoute renders
 * the route itself, and Login.js uses onSignedIn.
 *
 * This tab's stored session for the school first (no sign-in at all), then
 * silent (hidden iframe); if that fails, a button opens the same flow in a
 * popup.
 */

const PROVIDER_LABEL = { schoolBytes: 'School Bytes', sentral: 'Sentral' };

/** Step log for diagnosing the iframe sign-in in the console. Never logs tokens. */
const trace = (...args) => console.info('[lsgo-embed]', ...args);

function decodeJwt(token) {
    try {
        return JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    } catch {
        return null;
    }
}

/**
 * Sign-in worked but the LSGO profile didn't load: shown as itself (with the
 * real status and reason), never as "sign in again". `retryable`: a "Try
 * again" can help (the call failed), as opposed to an account with no linked
 * LSGO profile.
 */
export class ProfileLoadError extends Error {
    constructor(message, { status = null, reason = null, retryable = true } = {}) {
        super(message);
        this.status = status;
        this.reason = reason;
        this.retryable = retryable;
    }
}

/**
 * HTTP status and the server's reason from a failed call, in either shape:
 * config/axios resolves some failures as the response (or the axios error)
 * instead of rejecting, and a rejection carries it on error.response.
 */
export function failureDetail(resultOrError) {
    const res = resultOrError?.response ?? resultOrError;
    const status = Number(res?.status) || null;
    const data = res?.data;
    const text = (v) => (typeof v === 'string' && v.trim() ? v.trim() : null);
    const reason =
        text(data) ||
        text(data?.message) ||
        text(data?.error_description) ||
        text(data?.error) ||
        text(data?.error?.message) ||
        text(data?.code) ||
        (status ? null : text(resultOrError?.message));
    return { status, reason };
}

function profileLoadError(resultOrError) {
    const { status, reason } = failureDetail(resultOrError);
    const detail = [status && `HTTP ${status}`, reason].filter(Boolean).join(': ');
    return new ProfileLoadError(
        `Signed in, but your LSGO profile couldn't be loaded${detail ? ` (${detail})` : ''}.`,
        { status, reason }
    );
}

export async function loadTeacher(accessToken) {
    const claims = decodeJwt(accessToken);
    if (!claims?.legacy_user_id) {
        throw new ProfileLoadError('This account has no linked LSGO profile.', { retryable: false });
    }
    let profile;
    try {
        profile = await Users.getUserProfile(claims.legacy_user_id);
    } catch (err) {
        throw profileLoadError(err);
    }
    if (!profile?.data || !(Number(profile.status) < 400)) throw profileLoadError(profile);
    if (!profile.data.name) profile.data.name = profile.data.firstName;
    store.dispatch(setCurrentTeacher({ ...profile.data }));
    trace('profile loaded, signed in:', store.getState().auth?.isAuthenticated);
}

/** `tenant`: lsgo_school_<id> for the SIS school (utils/sisSchoolAuth.js). */
const LsgoAuthEmbedSignIn = ({ ssoType, tenant, onSignedIn }) => {
    const idp = idpForSsoType(ssoType);
    const [status, setStatus] = React.useState('signing-in'); // 'signing-in' | 'needs-click' | 'error'
    const [error, setError] = React.useState(null);
    const [canRetry, setCanRetry] = React.useState(false);
    // Bumped by "Try again": reruns the sign-in below, which reuses this tab's stored session.
    const [attempt, setAttempt] = React.useState(0);

    async function finish(accessToken) {
        await loadTeacher(accessToken);
        if (onSignedIn) onSignedIn();
    }

    React.useEffect(() => {
        let cancelled = false;
        onEmbedSessionExpired(() => {
            trace('session could not be refreshed');
            setError('Your session ended.');
            setStatus('needs-click');
        });
        (async () => {
            trace('start', { idp, tenant });
            try {
                const restored = await restoreEmbedSession({ tenant });
                trace(restored ? 'reusing this tab\'s session' : 'no session to reuse, signing in');
                const accessToken = restored ?? (await silentSignIn({ idp, tenant }));
                if (!restored) trace('silent sign-in ok', { cancelled });
                if (!cancelled) await finish(accessToken);
            } catch (err) {
                if (!cancelled) showFailure(err);
            }
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idp, tenant, attempt]);

    const tryAgain = () => {
        setError(null);
        setCanRetry(false);
        setStatus('signing-in');
        setAttempt((n) => n + 1);
    };

    // Only a sign-in that didn't happen offers the button again; a linking
    // or profile problem is shown as what it is.
    function showFailure(err) {
        trace('failed:', err?.message);
        if (err instanceof ProfileLoadError) {
            setError(err.message);
            setCanRetry(err.retryable);
            setStatus('error');
        } else if (err.message === 'access_denied') {
            setError(`Your ${PROVIDER_LABEL[ssoType] ?? ''} account isn't linked to an LSGO account.`);
            setStatus('error');
        } else {
            setError(
                err.message === 'popup_blocked'
                    ? 'Please allow pop-ups to sign in.'
                    : `Couldn't sign you in automatically (${err.message || 'unknown error'}).`
            );
            setStatus('needs-click');
        }
    }

    const signInWithPopup = () => {
        setStatus('signing-in');
        // Called straight from the click: popupSignIn opens the window synchronously.
        popupSignIn({ idp, tenant })
            .then(finish)
            .catch(showFailure);
    };

    if (!idp) return <Alert severity='error'>Unsupported sign-in provider.</Alert>;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, p: 4 }}>
            {status === 'signing-in' && (
                <>
                    <CircularProgress size={28} />
                    <Typography>Signing you in…</Typography>
                </>
            )}
            {status === 'needs-click' && (
                <>
                    {error && <Alert severity='warning'>{error}</Alert>}
                    <Button variant='contained' onClick={signInWithPopup}>
                        Sign in with {PROVIDER_LABEL[ssoType] ?? 'your school'}
                    </Button>
                </>
            )}
            {status === 'error' && (
                <>
                    <Alert severity='error'>{error}</Alert>
                    {canRetry && (
                        <Button variant='contained' onClick={tryAgain}>
                            Try again
                        </Button>
                    )}
                </>
            )}
        </Box>
    );
};

export default LsgoAuthEmbedSignIn;
